import { Node, Edge } from '@xyflow/react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  validateAndConvertEdges, 
  initialNodes 
} from "./utils";
import { checkAccountAccess, getAccountOwnershipInfo } from "./accountService";
import { getNavigationSourceForKpiCreation, resetNavigationSource } from "./navigationService";
import { createKpiRecordsFromCustomerData } from "./kpiCreationService";
import React from 'react';

type Account = {
  company_id?: string;
  region: string;
  country: string;
  company_name: string;
  parent_company?: string;
  industry: string;
  account_manager: string;
};

// Save individual node edits - only allow account owners to edit
export const saveNodeEdit = async (
  nodeId: string | null,
  title: string,
  description: string,
  companyId: string,
  setNodes: (updater: (nodes: Node[]) => Node[]) => void,
  handleNodeDoubleClick: (event: React.MouseEvent, nodeId: string) => void
) => {
  if (!nodeId) return;
  
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");
    
    // Check account ownership info
    const ownershipInfo = await getAccountOwnershipInfo(companyId);
    if (!ownershipInfo.canEdit) {
      toast.error("Only account owners can edit nodes");
      return;
    }
    
    // Extract actual ID from the nodeId
    const actualId = nodeId.replace(/^kpi-(?:exec|financial|initiative|operating)-/, '').replace(/^kpi-/, '');
    
    // Update the node in Supabase using the account owner's user_id
    const { error } = await supabase
      .from('business_value_framework')
      .update({
        title: title,
        description: description
      })
      .eq('company_id', companyId)
      .eq('user_id', ownershipInfo.accountOwnerUserId)
      .or(`id.eq.${actualId},title.eq.${title}`);
      
    if (error) throw error;
    
    // Update the node in the local state
    setNodes(currentNodes => 
      currentNodes.map(node => {
        if (node.id === nodeId) {
          const updatedNode = {
            ...node,
            data: {
              ...node.data,
              title: title,
              description: description,
              label: {
                type: 'node-label',
                title: title,
                description: description
              },
              onNodeDoubleClick: handleNodeDoubleClick
            }
          };
          return updatedNode;
        }
        return node;
      })
    );
    
    toast.success("Node updated successfully");
  } catch (error) {
    console.error('Error saving node edit:', error);
    toast.error("Failed to save node changes");
  }
};

// Simplified framework fetching - reads existing data and handles KPI creation based on navigation
export const fetchFramework = async (
  companyId: string,
  setNodes: (updater: (nodes: Node[]) => Node[]) => void,
  setEdges: (edges: Edge[]) => void,
  updateRectangleWidths: (nodes: Node[]) => void,
  handleNodeDoubleClick: (event: React.MouseEvent, nodeId: string) => void,
  setHasLoadedKpiData: (loaded: boolean) => void
) => {
  try {
    console.log("=== Starting fetchFramework ===");
    console.log("Company ID:", companyId);
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.log("No authenticated user");
      return;
    }
    
    // Get account ownership info
    const ownershipInfo = await getAccountOwnershipInfo(companyId);
    if (!ownershipInfo.canView) {
      console.error('User does not have permission to view this account');
      return;
    }
    
    console.log("Account owner:", ownershipInfo.accountOwnerUserId);
    console.log("Is owner:", ownershipInfo.isOwner);
    
    // Check navigation source to determine KPI creation behavior
    const navigationSource = getNavigationSourceForKpiCreation();
    console.log("Navigation source:", navigationSource);
    
    // If coming from Customer KPIs tab and user is owner, create KPI records
    if (navigationSource === 'customer-kpis' && ownershipInfo.isOwner) {
      console.log("Coming from Customer KPIs - attempting to create KPI records");
      const wasCreated = await createKpiRecordsFromCustomerData(
        companyId,
        setNodes,
        updateRectangleWidths,
        handleNodeDoubleClick
      );
      
      if (wasCreated) {
        // Load edges if any exist
        const { data: frameworkData, error: frameworkError } = await supabase
          .from('business_value_framework')
          .select('edges')
          .eq('company_id', companyId)
          .eq('user_id', ownershipInfo.accountOwnerUserId)
          .limit(1)
          .maybeSingle();
        
        if (!frameworkError && frameworkData && frameworkData.edges) {
          const validatedEdges = validateAndConvertEdges(frameworkData.edges);
          setEdges(validatedEdges);
        }
        
        setHasLoadedKpiData(true);
        resetNavigationSource();
        console.log("KPI records created successfully");
        return;
      }
    }
    
    // Default behavior: Read existing framework data only
    console.log("Reading existing framework data only");
    
    const { data: frameworkData, error: frameworkError } = await supabase
      .from('business_value_framework')
      .select('*')
      .eq('company_id', companyId)
      .eq('user_id', ownershipInfo.accountOwnerUserId)
      .order('created_at', { ascending: true });
    
    if (frameworkError) {
      console.error('Error fetching framework:', frameworkError);
      return;
    }
    
    console.log("Fetched framework records:", frameworkData?.length || 0);
    
    if (frameworkData && frameworkData.length > 0) {
      // Create nodes from existing framework data
      const loadedNodes = frameworkData
        .filter(item => item.title && item.title.trim() !== '')
        .map(item => {
          // Standardized ID generation logic
          let nodeId;
          if (item.kpi_type && ['Executive KPIs', 'Core Financial KPIs', 'Business Initiatives', 'Operating KPIs'].includes(item.kpi_type)) {
            const typePrefix = item.kpi_type.toLowerCase().replace(/\s+/g, '-').replace('kpis', '').replace('initiatives', 'initiative');
            nodeId = `kpi-${typePrefix}-${item.id}`;
          } else {
            nodeId = `node-${item.id}`;
          }

          return {
            id: nodeId,
            type: 'default',
            data: {
              label: item.kpi_type ? {
                type: 'node-label',
                title: item.title,
                description: item.description || ''
              } : item.title,
              title: item.title,
              description: item.description || '',
              kpiType: item.kpi_type,
              onNodeDoubleClick: handleNodeDoubleClick,
              companyId: companyId
            },
            position: {
              x: item.position_x || 0,
              y: item.position_y || 0
            },
            style: {
              width: 200,
              height: 80,
              minWidth: 200,
              maxWidth: 200,
              minHeight: 80,
              maxHeight: 80,
              backgroundColor: 'white',
              padding: '8px',
              borderRadius: '8px'
            }
          };
        });
      
      console.log("Created loaded nodes:", loadedNodes.length);
      
      // Combine with initial nodes
      const allNodes = [...initialNodes, ...loadedNodes].map(node => ({
        ...node,
        data: {
          ...node.data,
          onNodeDoubleClick: handleNodeDoubleClick,
          companyId: companyId
        }
      }));
      
      setNodes(() => allNodes);
      updateRectangleWidths(allNodes);
      
      // Handle edges
      const edgesData = frameworkData.find(fd => fd.edges);
      if (edgesData && edgesData.edges) {
        const validatedEdges = validateAndConvertEdges(edgesData.edges);
        setEdges(validatedEdges);
      }
      
      const kpiNodes = frameworkData.filter(item => 
        item.kpi_type && ['Executive KPIs', 'Core Financial KPIs', 'Business Initiatives', 'Operating KPIs'].includes(item.kpi_type)
      );
      setHasLoadedKpiData(kpiNodes.length > 0);
      
      console.log("Loaded existing framework successfully");
    } else {
      // No framework data, just load initial nodes
      console.log("No framework data found, loading initial nodes only");
      
      const initialNodesWithHandlers = initialNodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          onNodeDoubleClick: handleNodeDoubleClick,
          companyId: companyId
        }
      }));
      
      setNodes(() => initialNodesWithHandlers);
      updateRectangleWidths(initialNodesWithHandlers);
      setHasLoadedKpiData(false);
    }
    
    // Reset navigation source after processing
    resetNavigationSource();
    console.log("=== fetchFramework completed ===");
    
  } catch (error) {
    console.error('Error fetching framework data:', error);
    toast.error("Failed to load framework data");
    resetNavigationSource();
  }
};

// Add a new KPI node (for Potential Solutions only) - only allow account owners to add
export const addKPINode = async (
  kpiType: string,
  kpiTitle: string,
  kpiDescription: string,
  selectedAccount: Account,
  nodes: Node[],
  setNodes: (updater: (nodes: Node[]) => Node[]) => void,
  updateRectangleWidths: (nodes: Node[]) => void,
  handleNodeDoubleClick: (event: React.MouseEvent, nodeId: string) => void
): Promise<boolean> => {
  if (!kpiType || !kpiTitle.trim() || !selectedAccount.company_id) {
    toast.error("Please fill in all required fields");
    return false;
  }
  
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("User not authenticated");
      return false;
    }
    
    // Check account ownership info
    const ownershipInfo = await getAccountOwnershipInfo(selectedAccount.company_id);
    if (!ownershipInfo.canEdit) {
      toast.error("Only account owners can add KPI nodes");
      return false;
    }
    
    // Map kpiType to the correct database value
    let dbKpiType = kpiType;
    if (kpiType === 'solutions') {
      dbKpiType = 'Potential Solutions';
    }
    
    // Calculate position for new node
    const existingKpiNodes = nodes.filter(node => 
      node.data.kpiType === kpiType || 
      (kpiType === 'solutions' && node.id.startsWith('kpi-') && !node.data.kpiType)
    );
    
    const baseY = kpiType === 'Executive KPIs' ? 25 :
                  kpiType === 'Business Initiatives' ? 135 :
                  kpiType === 'Core Financial KPIs' ? 245 :
                  kpiType === 'Operating KPIs' ? 355 : 505;
    
    const newX = 450 + (existingKpiNodes.length * 250);
    
    // Insert into Supabase using the account owner's user_id with correct kpi_type
    const { data, error } = await supabase
      .from('business_value_framework')
      .insert({
        company_id: selectedAccount.company_id,
        title: kpiTitle,
        description: kpiDescription || '',
        position_x: newX,
        position_y: baseY,
        kpi_type: dbKpiType, // Use the mapped kpi_type value
        user_id: ownershipInfo.accountOwnerUserId
      })
      .select()
      .single();
    
    if (error) throw error;
    
    // Create new node with proper ID from database
    const newNode: Node = {
      id: `kpi-${data.id}`,
      type: 'default',
      data: {
        label: {
          type: 'node-label',
          title: kpiTitle,
          description: kpiDescription || ''
        },
        title: kpiTitle,
        description: kpiDescription || '',
        kpiType: kpiType,
        onNodeDoubleClick: handleNodeDoubleClick,
        companyId: selectedAccount.company_id
      },
      position: { x: newX, y: baseY },
      style: {
        width: 200,
        height: 80,
        minWidth: 200,
        maxWidth: 200,
        minHeight: 80,
        maxHeight: 80,
        backgroundColor: 'white',
        padding: '8px',
        borderRadius: '8px'
      }
    };
    
    // Add to nodes
    const updatedNodes = [...nodes, newNode];
    setNodes(() => updatedNodes);
    updateRectangleWidths(updatedNodes);
    
    toast.success("KPI node added successfully");
    return true;
    
  } catch (error) {
    console.error('Error adding KPI node:', error);
    toast.error("Failed to add KPI node");
    return false;
  }
};
